import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../api/http";
import { useAuditProgress } from "../hooks/useAuditProgress";
import { useAuth } from "../context/AuthContext";
import { useActiveSite } from "../context/ActiveSiteContext";
import AuthCallout from "../components/AuthCallout";
import SimpleUrlInput from "../components/SimpleUrlInput";
import { useToast } from "../context/ToastContext";
import { downloadCsv } from "../utils/csv";
import { isAuthError, parseApiError } from "../utils/apiErrors";
import { isValidWebsiteUrl } from "../utils/urlValidation";
import DataTable from "../components/shared/DataTable";

const csvColumns = [
  { key: "url", label: "Broken URL" },
  { key: "status", label: "Status" },
];

const demoBrokenLinks = [
  { url: "https://web.dev/does-not-exist", status: 404 },
  { url: "https://web.dev/old-guides/performance", status: 301 },
  { url: "https://web.dev/static/missing-image.png", status: 404 },
  { url: "https://web.dev/assets/archive/style.css", status: 410 },
];

const BrokenLinks = () => {
  const { isAuthenticated } = useAuth();
  const { showError } = useToast();
  const { activeSite, refreshSites } = useActiveSite();
  const [url, setUrl] = useState("");
  const [brokenLinks, setBrokenLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState("");
  const { progress, subscribe, reset } = useAuditProgress();

  useEffect(() => {
    if (activeSite) {
      setUrl(activeSite.url);
    }
  }, [activeSite]);

  useEffect(() => {
    if (progress?.done && !checked) {
      setBrokenLinks(progress.result?.brokenLinks || progress.partialLinks || []);
      setChecked(true);
      setLoading(false);
      setError("");
      refreshSites();
    }
  }, [checked, progress, refreshSites]);

  useEffect(() => {
    if (progress?.error) {
      setLoading(false);
      setChecked(true);
      setError(progress.error);
    }
  }, [progress]);

  const checkLinks = async () => {
    if (!url || !isAuthenticated) return;
    if (!isValidWebsiteUrl(url)) {
      showError("Please enter a valid URL.");
      return;
    }

    setLoading(true);
    setChecked(false);
    setBrokenLinks([]);
    setError("");
    reset();

    try {
      const { data } = await apiPost("/api/seo/check-broken-links/start", { url });
      if (data.auditId) {
        await subscribe(data.auditId, { progressPath: "/api/seo/progress" });
        return;
      }
      throw new Error("Broken link scan did not start");
    } catch (err) {
      try {
        const { data } = await apiGet("/api/seo/check-broken-links", { url });
        setBrokenLinks(data.brokenLinks || []);
        setChecked(true);
        refreshSites();
      } catch (fallbackErr) {
        const message = parseApiError(fallbackErr, "Failed to check broken links");
        setError(isAuthError(fallbackErr) ? `${message}. Please sign in and try again.` : message);
      } finally {
        setLoading(false);
      }
    }
  };

  const currentLinks = useMemo(
    () => (progress && !progress.done ? progress.partialLinks || [] : brokenLinks),
    [brokenLinks, progress]
  );
  const displayLinks = !isAuthenticated && currentLinks.length === 0 ? demoBrokenLinks : currentLinks;

  const exportResults = () => {
    if (displayLinks.length === 0) return;
    downloadCsv("broken-links.csv", csvColumns, displayLinks);
  };

  return (
    <div className="page">
      <h1 className="page-title">Broken Links Checker</h1>
      <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
        Scan a site for broken internal and external links, then export the results as CSV.
      </p>

      <AuthCallout feature="broken-link scans" />

      <div className="card">
        <div className="input-group">
          <SimpleUrlInput
            value={url}
            onChange={setUrl}
            onKeyDown={(e) => e.key === "Enter" && checkLinks()}
            aria-label="Website URL to scan for broken links"
            disabled={!isAuthenticated}
          />
          <button className="btn btn-primary" onClick={checkLinks} disabled={!isAuthenticated || loading || !url}>
            {loading ? "Scanning..." : "Check Links"}
          </button>
        </div>

        {loading && progress ? (
          <div className="progress-container" style={{ marginTop: "20px", marginBottom: "20px" }}>
            <div className="progress-bar-track">
              <div
                className="progress-bar-fill"
                style={{ width: `${Math.min(100, Math.max(5, progress.percent || 0))}%` }}
              />
            </div>
            <div className="progress-info">
              <span className="progress-label">{progress.label || "Scanning..."}</span>
            </div>
          </div>
        ) : null}

        {loading && !progress ? <div className="loading">Initializing scanner...</div> : null}
        {error ? <div className="progress-error">{error}</div> : null}

        {checked && !loading && displayLinks.length === 0 && !error ? (
          <div className="empty-state">No broken links found. Your site looks healthy.</div>
        ) : null}

        {!isAuthenticated ? (
          <div className="site-panel-empty" style={{ marginBottom: 16, background: 'rgba(37, 99, 235, 0.05)', border: '1px dashed var(--accent)' }}>
            Demo preview for <strong>https://web.dev</strong>. Sign in to scan your own site and export real results.
          </div>
        ) : null}

        {displayLinks.length > 0 ? (
          <>
            <div className="section-actions" style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 className="page-subtitle" style={{ margin: 0 }}>Broken Links Found ({displayLinks.length})</h3>
                <span className="pagination-info">Exportable for handoff or triage.</span>
              </div>
              {isAuthenticated && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={exportResults}>
                  Export CSV
                </button>
              )}
            </div>
            
            <DataTable
              headers={[
                { key: "url", label: "Broken URL" },
                { key: "status", label: "Status Code", render: (r) => (
                  <span className="status-badge error">{r.status || "Error"}</span>
                ) }
              ]}
              data={displayLinks}
              keyExtractor={(r, i) => `${r.url}-${i}`}
            />
          </>
        ) : null}
      </div>
    </div>
  );
};

export default BrokenLinks;
